import React, { useMemo } from 'react';
import styled from 'styled-components';
import { dayjs } from '@/utils/dayjs';
import { cssVars } from '../../../app/styles';
import {
  GANTT_TIMELINE_VERTICAL_PADDING_PX,
  MONTH_SEPARATION_HEADER_PADDING_PX,
  SEPARATOR_HEIGHT,
} from '../../constants';
import { listingCalendarActivityToBookingEvents } from '../../utils/calendar-event-generators/booking-activity-events';
import { getListingLiveCompleteEvents } from '../../utils/calendar-event-generators/listing-live-events';
import { GanttTimeline } from './gantt-timeline';
import {
  DailyCalendarContainer,
  DailyCalendarGrid,
  DayOfMonthLabelEventContainer,
} from './styles';

const StyledDailyCalendarGrid = styled(DailyCalendarGrid)`
  border-bottom: ${SEPARATOR_HEIGHT} solid
    ${cssVars.propertyHistoryColors.swimlaneLabelPaneBg};
`;

const PAD_DATES_WITH_ZERO = '0';

/**
 * Emulates a white segment of the border between months where the border
 * is adjacent to the grey list of dates. Since the real border is otherwise
 * grey itself, this "fake" border is here for better contrast.
 */
const FakeBorder = styled.div`
  background: #fff;
  width: ${MONTH_SEPARATION_HEADER_PADDING_PX}px;
  height: calc(100% + ${GANTT_TIMELINE_VERTICAL_PADDING_PX * 2}px);
  position: absolute;
  z-index: 999;
  top: -${GANTT_TIMELINE_VERTICAL_PADDING_PX * 2}px;
  right: -${MONTH_SEPARATION_HEADER_PADDING_PX}px;
`;

function makeDayOfMonthLabelEvents(daysInMonth) {
  return new Array(daysInMonth).fill(null).map((_, i) => ({
    startDay: i + 1,
    endDay: i + 1,
    contents: (
      <DayOfMonthLabelEventContainer>
        {(i + 1).toString().padStart(2, PAD_DATES_WITH_ZERO)}
        {i === daysInMonth - 1 && <FakeBorder />}
      </DayOfMonthLabelEventContainer>
    ),
    id: i,
  }));
}

const FULL_MONTH_NAME_FORMAT = 'MMMM';

export const DailyCalendar = ({
  calendarActivity,
  firstDayOfMonth,
  listingLiveEvents,
  monthIndex,
}) => {
  const daysInMonth = dayjs(firstDayOfMonth).daysInMonth();

  const monthName = dayjs(firstDayOfMonth).format(FULL_MONTH_NAME_FORMAT);
  const bookingEvents = listingCalendarActivityToBookingEvents(
    calendarActivity,
    monthName
  );

  const dateLabelEvents = makeDayOfMonthLabelEvents(daysInMonth);

  const listingLiveEventsToDisplay = useMemo(
    () => listingLiveEvents.map(getListingLiveCompleteEvents),
    [listingLiveEvents]
  );

  const isFirstMonthInCalendar = monthIndex === 0;

  return (
    <DailyCalendarContainer>
      <StyledDailyCalendarGrid
        numDays={daysInMonth}
        suppressLeftBorder={isFirstMonthInCalendar}
      >
        <GanttTimeline timelineIndex={0} events={dateLabelEvents} />
        {listingLiveEventsToDisplay.map(({ listing, events }, i) => (
          <GanttTimeline
            key={listing.id}
            timelineIndex={i + 1}
            events={events}
          />
        ))}
        <GanttTimeline
          timelineIndex={listingLiveEventsToDisplay.length + 1}
          events={bookingEvents}
        />
      </StyledDailyCalendarGrid>
    </DailyCalendarContainer>
  );
};
